import {
  ActionIcon,
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";

/**
 * The beacon install snippet. This is the only place in the app that renders it.
 *
 * It lives in its own component because it used to sit inside BrandDetailsSetup, which
 * SettingsPage renders only once a brand is Active — so a brand still in setup could not
 * install its pixel, could therefore never collect traffic, and so could never satisfy the
 * thing it was waiting on. It belongs in the wizard as much as in settings.
 */
export default function TrackingSnippet({ brand, withHeading = true }) {
  // document.referrer has to travel as a query parameter: a browser cannot set a
  // cross-origin Referer header, and for an <img> beacon that header is this page,
  // not where the visitor came from. Without it every visit classifies as "direct"
  // and the search / social / LLM traffic buckets stay empty.
  // Forwards only the known attribution parameters, by name. Appending the landing
  // page's whole query string would let a page carrying ?url= or ?event= duplicate the
  // beacon's own keys — ASP.NET comma-joins duplicates, so the tracked URL would arrive
  // mangled — and would also send the query string twice (once encoded inside url=),
  // pushing long URLs past the request-line limit where the pageview is silently dropped.
  const snippet = `<script>
(function(){
  var A=["utm_source","utm_medium","utm_campaign","utm_term","utm_content","utm_id",
         "gclid","gbraid","wbraid","msclkid","fbclid","ttclid","li_fat_id","ref"];
  var p=new URLSearchParams(location.search);
  var q="projectId=${brand.id}&event=pageview";
  q+="&url="+encodeURIComponent(location.href);
  if(document.referrer)q+="&referrer="+encodeURIComponent(document.referrer);
  A.forEach(function(k){
    var v=p.get(k);
    if(v)q+="&"+k+"="+encodeURIComponent(v);
  });
  new Image().src="${window.location.origin}/api/v1/track?"+q;
})();
</script>`;

  return (
    <>
      {withHeading && (
        <Title mt="lg" order={2}>
          Tracking Pixel
        </Title>
      )}

      <Alert color="blue" mt="md">
        <Text size="sm" mb="sm">
          Add this snippet to your website's <Code>&lt;head&gt;</Code> tag to track pageviews and
          traffic sources.
        </Text>

        <Group gap="xs" mb="xs">
          <Text size="sm" fw={500}>Brand ID:</Text>
          <Code>{brand.id}</Code>
          <CopyButton value={brand.id}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? "Copied" : "Copy"}>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  onClick={copy}
                  color={copied ? "green" : "gray"}
                >
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>

        <CopyButton value={snippet}>
          {({ copied, copy }) => (
            <Stack gap={4}>
              <Code block style={{ fontSize: 12 }}>
                {snippet}
              </Code>
              <Group justify="flex-end">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  color={copied ? "green" : "blue"}
                  onClick={copy}
                >
                  {copied ? "Copied!" : "Copy snippet"}
                </Button>
              </Group>
            </Stack>
          )}
        </CopyButton>

        <Text size="xs" c="dimmed" mt="xs">
          The pixel will send anonymous pageview events back to BrandKarma so you can track
          traffic sources and visitor trends.
        </Text>
      </Alert>
    </>
  );
}
